import React, { useState } from 'react';
import { X, Upload, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { submitBankTransfer } from '@/services/financialService';

interface BankTransferUploadModalProps {
  accountId: string;
  /** Set when a parent submits on behalf of their child */
  studentId?: string;
  balance?: number; // Remaining balance to pay
  onClose: () => void;
  onSuccess: () => void;
}

export const BankTransferUploadModal: React.FC<BankTransferUploadModalProps> = ({
  accountId,
  studentId,
  balance,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    amount: '',
    transferDate: '',
    reference: '',
    notes: '',
  });
  const [receipt, setReceipt] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!receipt) {
      setError('Veuillez sélectionner un reçu de virement');
      return;
    }

    // Validate file size (5MB max)
    if (receipt.size > 5 * 1024 * 1024) {
      setError('Le fichier est trop volumineux (max 5 MB)');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(receipt.type)) {
      setError('Format de fichier non supporté. Utilisez JPG, PNG ou PDF');
      return;
    }

    // Validate amount
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Le montant doit être supérieur à 0');
      return;
    }

    // Validate against balance
    if (balance !== undefined && amount > balance) {
      setError(`Le montant (${amount.toLocaleString('fr-MA')} MAD) dépasse le solde restant (${balance.toLocaleString('fr-MA')} MAD)`);
      return;
    }

    try {
      setSubmitting(true);
      await submitBankTransfer({
        accountId,
        ...(studentId ? { studentId } : {}),
        amount: parseFloat(formData.amount),
        transferDate: formData.transferDate,
        reference: formData.reference,
        notes: formData.notes,
        receipt,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la soumission du virement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setReceipt(file);
      setError(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Upload className="w-5 h-5 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Soumettre un virement bancaire</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Info Message */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              Votre virement sera vérifié par le service financier avant d'être approuvé.
              Vous recevrez une notification une fois le traitement effectué.
            </p>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Montant (MAD) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: 5000"
              required
              min="0"
              step="0.01"
              max={balance}
            />
            {balance !== undefined && balance > 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Solde restant à payer: <span className="font-semibold text-gray-700">{balance.toLocaleString('fr-MA')} MAD</span>
              </p>
            )}
          </div>

          {/* Transfer Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date du virement <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.transferDate}
              onChange={(e) => setFormData({ ...formData, transferDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              max={new Date().toISOString().split('T')[0]} // Can't be in the future
            />
          </div>

          {/* Reference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Référence du virement
            </label>
            <input
              type="text"
              value={formData.reference}
              onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: TRF123456789"
            />
            <p className="text-xs text-gray-500 mt-1">
              Numéro de référence indiqué sur votre reçu (optionnel)
            </p>
          </div>

          {/* Receipt Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reçu de virement <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-blue-400 transition-colors">
              <div className="space-y-1 text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                  >
                    <span>Choisir un fichier</span>
                    <input
                      id="file-upload"
                      type="file"
                      className="sr-only"
                      accept="image/jpeg,image/jpg,image/png,application/pdf"
                      onChange={handleFileChange}
                      required
                    />
                  </label>
                  <p className="pl-1">ou glisser-déposer</p>
                </div>
                <p className="text-xs text-gray-500">
                  JPG, PNG ou PDF jusqu'à 5 MB
                </p>
                {receipt && (
                  <p className="text-sm text-green-600 font-medium mt-2">
                    ✓ {receipt.name} ({(receipt.size / 1024).toFixed(0)} KB)
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optionnel)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Informations supplémentaires..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={submitting}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting || !receipt}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Soumettre
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
